import { api } from "../App";
import { createNotification } from "../components/Notifications/Notifications";
import {
  GET_EDUCATIONAL_STRUCTURE,
  GET_BASIC_EDU_GROUP,
  GET_BASIC_EDU_COURSES,
  GET_DOP_EDU_GROUPS,
  GET_DOP_EDU_COURSES,
  GET_OUT_EDU_GROUPS,
  GET_OUT_EDU_COURSES,
  GET_ELECTIVE_GROUPS,
  GET_POSITION_HISTORY,
  GET_REPORT,
  SET_EDUCATION_TAB_LOADING,
} from "./types";

// Бэк возвращает ошибку в разных формах: { success:false, error } (400),
// { detail: "..." } / { detail: [...] } либо сырой массив ошибок валидации
// [{ msg, ... }] прямо в теле ответа (422) — приводим к одной читаемой строке.
const getApiErrorMessage = (ERR, fallback) => {
  const data = ERR?.response?.data;
  if (!data) return fallback;
  if (data.error) return data.error;
  if (typeof data.detail === 'string') return data.detail;
  const list = Array.isArray(data.detail) ? data.detail : (Array.isArray(data) ? data : null);
  if (list) {
    const reasons = list.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean);
    return reasons.length ? reasons.join('; ') : fallback;
  }
  return fallback;
};

export const getEducationalStructures = () => async (dispatch) => {
  try {
    const { data } = await api.get(`/educational_structure/nodes`);
    dispatch({ type: GET_EDUCATIONAL_STRUCTURE, payload: data });
  } catch (ERR) {}
};

// Вкладка "Основное образование" — классы
export const getBasicEduGroup = (id) => async (dispatch) => {
  try {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: true });
    const { data } = await api.get(`/educational_structure/nodes/${id}/main/groups`);
    dispatch({ type: GET_BASIC_EDU_GROUP, payload: data });
  } catch (error) {
    dispatch({ type: GET_BASIC_EDU_GROUP, payload: [] });
  } finally {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: false });
  }
};

// Вкладка "Основное образование" — предметы
export const getBasicEduCourses = (id, withClosed = false) => async (dispatch) => {
  try {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: true });
    const { data } = await api.get(`/educational_structure/nodes/${id}/main/courses`, {
      params: { closed: withClosed ? true : false },
    });
    dispatch({ type: GET_BASIC_EDU_COURSES, payload: data });
  } catch (error) {
    dispatch({ type: GET_BASIC_EDU_COURSES, payload: [] });
  } finally {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: false });
  }
};

// Вкладка "Доп. образование" — группы
export const getDopEduGroups = (id) => async (dispatch) => {
  try {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: true });
    const { data } = await api.get(`/educational_structure/nodes/${id}/do/groups`);
    dispatch({ type: GET_DOP_EDU_GROUPS, payload: data });
  } catch (error) {
    dispatch({ type: GET_DOP_EDU_GROUPS, payload: [] });
  } finally {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: false });
  }
};

// Вкладка "Доп. образование" — предметы
export const getDopEduCourses = (id) => async (dispatch) => {
  try {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: true });
    const { data } = await api.get(`/educational_structure/nodes/${id}/do/courses`);
    dispatch({ type: GET_DOP_EDU_COURSES, payload: data });
  } catch (error) {
    dispatch({ type: GET_DOP_EDU_COURSES, payload: [] });
  } finally {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: false });
  }
};

// Вкладка "Внеурочная деятельность" — группы
export const getOutEduGroups = (id) => async (dispatch) => {
  try {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: true });
    const { data } = await api.get(`/educational_structure/nodes/${id}/vd/groups`);
    dispatch({ type: GET_OUT_EDU_GROUPS, payload: data });
  } catch (error) {
    dispatch({ type: GET_OUT_EDU_GROUPS, payload: [] });
  } finally {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: false });
  }
};

// Вкладка "Внеурочная деятельность" — предметы
export const getOutEduCourses = (id) => async (dispatch) => {
  try {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: true });
    const { data } = await api.get(`/educational_structure/nodes/${id}/vd/courses`);
    dispatch({ type: GET_OUT_EDU_COURSES, payload: data });
  } catch (error) {
    dispatch({ type: GET_OUT_EDU_COURSES, payload: [] });
  } finally {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: false });
  }
};

// Вкладка "Элективы" — группы
export const getElectiveGroups = (id) => async (dispatch) => {
  try {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: true });
    const { data } = await api.get(`/educational_structure/nodes/${id}/electives`);
    dispatch({ type: GET_ELECTIVE_GROUPS, payload: data });
  } catch (error) {
    dispatch({ type: GET_ELECTIVE_GROUPS, payload: [] });
  } finally {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: false });
  }
};

// История назначений
export const getPositionHistory = (did) => async (dispatch) => {
  try {
    const { data } = await api.get(`/educational_structure/nodes/${did}/position_history`);
    dispatch({ type: GET_POSITION_HISTORY, payload: data });
  } catch (error) {
    dispatch({ type: GET_POSITION_HISTORY, payload: [] });
  }
};

// Отчёт
export const getReport = (reportType, filterId = 0, persType = '') => async (dispatch) => {
  try {
    const { data } = await api.get(`/educational_structure/report`, {
      params: { type: reportType, filter_id: filterId || 0, pers_type: persType },
    });
    dispatch({ type: GET_REPORT, payload: data });
    return data;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка формирования отчёта'));
    return null;
  }
};

// Сохранить связи вкладки "Основное образование"
export const saveBasicEduLinks = (did, gids, cids) => async (dispatch) => {
  try {
    await Promise.all([
      api.put(`/educational_structure/nodes/${did}/main/groups`, gids.map(id => ({ gid: id }))),
      api.put(`/educational_structure/nodes/${did}/main/courses`, cids.map(id => ({ cid: id }))),
    ]);
    createNotification('success', 'Данные успешно сохранены');
    return true;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка сохранения данных'));
    return false;
  }
};

// Сохранить связи вкладки "Доп. образование"
// DO-группы используют поле xp_key (не gid)
export const saveDopEduLinks = (did, gids, cids) => async (dispatch) => {
  try {
    await Promise.all([
      api.put(`/educational_structure/nodes/${did}/do/groups`, gids.map(id => ({ xp_key: id }))),
      api.put(`/educational_structure/nodes/${did}/do/courses`, cids.map(id => ({ cid: id }))),
    ]);
    createNotification('success', 'Данные успешно сохранены');
    return true;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка сохранения данных'));
    return false;
  }
};

// Сохранить связи вкладки "Внеурочная деятельность"
// ВД-группы используют поле xp_key (не gid)
export const saveOutEduLinks = (did, gids, cids) => async (dispatch) => {
  try {
    await Promise.all([
      api.put(`/educational_structure/nodes/${did}/vd/groups`, gids.map(id => ({ xp_key: id }))),
      api.put(`/educational_structure/nodes/${did}/vd/courses`, cids.map(id => ({ cid: id }))),
    ]);
    createNotification('success', 'Данные успешно сохранены');
    return true;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка сохранения данных'));
    return false;
  }
};

// Сохранить связи вкладки "Элективы"
// Элективы хранятся в той же таблице что и основные классы (departments_groups).
// Чтобы не затереть основные классы при сохранении только элективов:
// 1. Читаем текущие main/groups
// 2. Оставляем те, которые НЕ входят в список всех возможных элективных gid-ов
// 3. Добавляем выбранные элективы и PUT
export const saveElectiveLinks = (did, selectedGids, allElectiveGids) => async (dispatch) => {
  try {
    const { data: currentMainGroups } = await api.get(`/educational_structure/nodes/${did}/main/groups`);
    const electiveSet = new Set(allElectiveGids);
    const nonElective = currentMainGroups
      .filter(g => !electiveSet.has(g.gid))
      .map(g => ({ gid: g.gid }));
    const combined = [...nonElective, ...selectedGids.map(gid => ({ gid }))];
    await api.put(`/educational_structure/nodes/${did}/main/groups`, combined);
    createNotification('success', 'Данные успешно сохранены');
    return true;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка сохранения данных'));
    return false;
  }
};

// Авто-простановка предметов по выбранным классам (не требует did в URL)
export const checkCoursesByClasses = (did, gids) => async (dispatch) => {
  try {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: true });
    const { data } = await api.post(`/educational_structure/mark_courses/by_classes`, { gids });
    return data;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка авто-простановки'));
    return null;
  } finally {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: false });
  }
};

// Авто-простановка предметов по выбранным группам ДО
export const checkCoursesByDopGroups = (did, gids) => async (dispatch) => {
  try {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: true });
    const { data } = await api.post(`/educational_structure/mark_courses/by_do_groups`, { gids });
    return data;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка авто-простановки'));
    return null;
  } finally {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: false });
  }
};

// Авто-простановка предметов по выбранным группам ВД
export const checkCoursesByOutGroups = (did, gids) => async (dispatch) => {
  try {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: true });
    const { data } = await api.post(`/educational_structure/mark_courses/by_vd_groups`, { gids });
    return data;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка авто-простановки'));
    return null;
  } finally {
    dispatch({ type: SET_EDUCATION_TAB_LOADING, payload: false });
  }
};

export const updateChildrensLine = (did, direction) => async (dispatch) => {
  try {
    await api.put(`/educational_structure/nodes/${did}/location/${direction}`);
    await dispatch(getEducationalStructures());
    createNotification('success', 'Данные успешно обновлены');
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка обновления данных'));
  }
};

export const updateChildrensLevel = (did, owner_did) => async (dispatch) => {
  try {
    await api.put(`/educational_structure/nodes/${did}/owner/${owner_did}`);
    await dispatch(getEducationalStructures());
    createNotification('success', 'Данные успешно обновлены');
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка обновления данных'));
  }
};

export const createChildren = (reqData) => async (dispatch) => {
  const { name, owner_did, mid, role, info, color, is_cycle, is_position, is_boss,
    category, code, not_in, application } = reqData;
  const body = { name, owner_did, mid, role, info, color, is_cycle, is_position, is_boss,
    category, code, not_in, application };
  try {
    await api.post(`/educational_structure/nodes`, body);
    createNotification('success', 'Успешно');
    await dispatch(getEducationalStructures());
    return true;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Не удалось создать запись'));
    return false;
  }
};

export const updateEducationalStructuresDepartmentsInfo = (reqData) => async (dispatch) => {
  // Отправляем только поля, известные API; лишние (teacherName, fio и пр.) убираем
  const { did, name, owner_did, mid, role, info, color, is_cycle, is_position, is_boss,
    category, code, not_in, application, sortorder } = reqData;
  const body = { name, owner_did, mid, role, info, color, is_cycle, is_position, is_boss,
    category, code, not_in, application, sortorder };
  try {
    await api.put(`/educational_structure/nodes/${did}`, body);
    await dispatch(getEducationalStructures());
    createNotification('success', 'Данные успешно обновлены');
    return true;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка обновления данных'));
    return false;
  }
};

// Собрать did узла и всех его потомков в порядке "листья сначала" —
// backend запрещает удалять узел, у которого остались дочерние узлы (п.13.7),
// поэтому перед удалением родителя нужно сначала удалить всех потомков.
const collectDidsLeavesFirst = (node) => {
  const dids = [];
  (node.children || []).forEach((child) => dids.push(...collectDidsLeavesFirst(child)));
  dids.push(node.data.did);
  return dids;
};

export const deleteEducationalStructuresDepartments = (node) => async (dispatch) => {
  const dids = collectDidsLeavesFirst(node);
  try {
    for (const did of dids) {
      await api.delete(`/educational_structure/nodes/${did}`);
    }
    createNotification('success', 'Успешно');
    return true;
  } catch (ERR) {
    createNotification('error', getApiErrorMessage(ERR, 'Ошибка удаления'));
    return false;
  } finally {
    await dispatch(getEducationalStructures());
  }
};
